import { useTranslation } from 'next-i18next'
import Image from 'next/future/image'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectMe } from '../../features/auth/authReducer'
import { useCountryTaxRate } from '../../hooks/useCountryTaxRate'
import Link from 'next/link'
import useCloudPrice from '../../hooks/useCloudPrice'

export default function PluginAddToCartLicenses ({ plugin }) {
  // External
  const router = useRouter()
  const { cloudPricePeriod } = useCloudPrice(plugin)
  const { t } = useTranslation('plugins')
  const me = useSelector(state => selectMe(state.auth))
  // States
  const [quantity, setQuantity] = useState(1)
  const [license, setLicense] = useState(0)
  const { initializeUserCountry, taxRate } = useCountryTaxRate(plugin)

  useEffect(() => {
    initializeUserCountry()
  }, [])

  // Reactive states
  const maxQuantity = useMemo(() => {
    // no quantity set in price => standard price and no need to search for max
    if (plugin.licenses[license].prices[0].quantity_to) {
      return Math.max(...plugin.licenses[license].prices.map(price => price.quantity_to))
    }
    return 99
  })
  const totalAmount = useMemo(() => {
    if (!plugin.licenses[license] || !plugin.licenses[license].prices.length) return 0

    // no quantity set in price => standard price and no need to search for the correct pricing
    if (plugin.licenses[license].prices[0].quantity_to) {
      const currentPrice = plugin.licenses[license].prices.filter(price => {
        return price.quantity_from <= quantity && price.quantity_to >= quantity
      })

      if (!currentPrice) {
        return 0
      }

      return quantity * currentPrice[0].price_net
    }

    return quantity * plugin.licenses[license].prices[0].price_net
  })

  // Checkout logic
  const redirectToShoppingCart = async () => {
    if (plugin.company.paymentMethods.length === 0) {
      return
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      {
        event: 'add_to_cart',
        ecommerce: {
          currency: 'EUR',
          value: (plugin.starting_price / 100),
          items: [
            {
              item_id: plugin.id,
              item_name: plugin.name,
              index: 0,
              price: (plugin.starting_price / 100),
              quantity,
              coupon: '-',
              discount: 0
            }
          ]
        }
      }
    )

    localStorage.setItem(
      'cart',
      JSON.stringify({
        plugin_id: plugin.id,
        quantity,
        amount: totalAmount ? totalAmount / 100 : 0, // Take the amount so we do not need to calculate it again
        license_id: plugin.licenses[license].id,
        company_id: plugin.company.id,
        company_slug: plugin.company.slug
      })
    )

    if (!me) {
      router.push('/cart/login')

      return
    }

    router.push('/cart')
  }

  return (
    <div className="row">
      {
        plugin.is_cloud_product && (
          <div className="col-12">
            <Link href={`/seller/${plugin.company.slug}?contactSubject=${encodeURIComponent(plugin.name)}#sellerContactForm`}>
              <a
                target="_blank"
                className="btn btn-primary w-100 position-relative"
              >
                <span>
                  {t('price_with_period', {
                    ns: 'plugins',
                    price: plugin.cloud_starting_price_net / 100,
                    period: cloudPricePeriod
                  })}
                  <br />
                  {t('cloud_product_contact_seller', { ns: 'plugins' })}
                </span>
              </a>
            </Link>
          </div>
        )
      }
      {
        plugin.is_on_request_only && (
          <div className="col-12">
            <Link href={`/seller/${plugin.company.slug}?contactSubject=${encodeURIComponent(plugin.name)}#sellerContactForm`}>
              <a
                target="_blank"
                className="btn btn-primary w-100 position-relative"
              >
                <span>
                  {t('one_time_payment_contact_seller', {
                    ns: 'plugins',
                    price: plugin.starting_price / 100
                  })}
                </span>
              </a>
            </Link>
          </div>
        )
      }
      {
        (!plugin.is_cloud_product && !plugin.is_on_request_only) && (
          <div className="col-12">
            <div className="row">
              <span className="d-block">
                {
                  taxRate?.getTaxRate
                    ? (
                      <div>
                        <span className="fs-1 text-dark-blue me-2 d-inline-block row">
                          <strong>
                            {
                            t('price_no_prefix', { price: totalAmount ? totalAmount / 100 : totalAmount })
                          }
                          </strong>
                        </span>
                        <span className="row fs-4 mx-1 mb-2">
                          {
                          t('price_no_prefix', { price: totalAmount ? (totalAmount + totalAmount * taxRate?.getTaxRate / 100) / 100 : totalAmount })
                        }
                          {
                            t('detail.cart.w_tax')
                          }
                        </span>
                      </div>
                      )
                    : (
                      <div>
                        <span className="fs-1 text-dark-blue me-2 mb-2 d-inline-block row">
                          <strong>
                            {
                            t('price_no_prefix', { price: totalAmount ? totalAmount / 100 : totalAmount })
                          }
                          </strong>
                        </span>
                        <span className="row mx-1 mb-2 d-inline-block text-muted fs-5">
                          {t('detail.cart.no_tax_disclaimer')}
                        </span>
                      </div>
                      )
                }
              </span>
              <div className="form-group col-md-8 col-12 mb-3">
                <label
                  className="form-label"
                  htmlFor="license"
                >
                  {t('detail.cart.license.label')}
                </label>
                <select
                  className="form-select"
                  id="license"
                  onChange={e => setLicense(e.target.value)}
                >
                  {
                    plugin.licenses && plugin.licenses.map((license, key) => (
                      <option
                        key={license.id}
                        value={key}
                      >
                        {license.names.find(name => name.language === router.locale.toUpperCase())?.name || license.names.find(name => name.name)?.name}
                      </option>
                    ))
                  }
                </select>
              </div>
              <div className="form-group col-md-4 col-12">
                <label
                  className="form-label"
                  htmlFor="license"
                >
                  {t('detail.cart.seats')}
                </label>
                <input
                  id="license"
                  type="number"
                  min={1}
                  max={maxQuantity}
                  className="form-control"
                  value={quantity}
                  onChange={e => setQuantity(e.target.value)}
                />
              </div>
              <div className="col-12 mt-3 position-relative">
                {
                  plugin.company.paymentMethods.length === 0 && (
                    <span
                      className="position-absolute top-0 end-0 translate-middle badge rounded-pill bg-warning p-0"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      data-bs-title={t('detail.cart.add_to_cart_not_payment_method_available')}
                      style={{ zIndex: 2 }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="black"
                        className="bi bi-info"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"
                        />
                      </svg>
                    </span>
                  )
                }
                <button
                  disabled={plugin.company.paymentMethods.length === 0}
                  className="btn btn-primary w-100 position-relative"
                  onClick={() => redirectToShoppingCart()}
                  style={{ zIndex: 1 }}
                >
                  <span className="me-3">
                    <Image
                      width={20}
                      height={20}
                      src="/images/icons/cart.png"
                      alt=""
                    />
                  </span>
                  <span>
                    {t('detail.cart.add_to_cart')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div>
  )
}
